import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, IconButton, Chip, CircularProgress, Alert } from '@mui/material';
import { ChevronLeft, ChevronRight, Check, Download } from '@mui/icons-material';
import api from '../../config/axiosConfig';

const ServiceCarousel = ({ onServiceSelect, selectedServices = [], compact = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  // Función para obtener subservicios de un servicio
  const fetchSubservices = async (serviceId) => {
    try {
      const response = await api.get(`/services/${serviceId}/subservices`);
      return response.data.filter(sub => sub.active === true);
    } catch (err) {
      console.error(`Error fetching subservices for service ${serviceId}:`, err);
      return [];
    }
  };


  const fetchCoveragePoints = async (serviceId) => {
    try {
      const response = await api.get(`/services/${serviceId}/coverage-points`);
      return (response.data || []).filter(p => p.included !== false);
    } catch (err) {
      return [];
    }
  };
  // Función para obtener servicios desde la API
  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/services');
      
      // Filtrar solo servicios activos
      const activeServicesData = response.data.filter(service => service.active === true);
      
      // Obtener subservicios para cada servicio
      const servicesWithSubservices = await Promise.all(
        activeServicesData.map(async (service, index) => {
          const [subservices, coveragePoints] = await Promise.all([
            fetchSubservices(service.id),
            fetchCoveragePoints(service.id)
          ]);
          
          return {
            id: service.id,
            title: service.serviceName || service.name || service.title,
            serviceName: service.serviceName || service.name || service.title,
            description: service.description || "Servicio profesional de alta calidad",
            price: parseFloat(service.subtotal) || 0,
            isHighlighted: index === 0, // Destacar el primer servicio
            features: coveragePoints.length > 0
              ? coveragePoints.map(p => p.description)
              : [
                  service.description || "Servicio profesional de alta calidad",
                  "Cumplimiento de normativas vigentes",
                  "Atención personalizada"
                ],
            subservices: subservices.map(sub => ({
              id: sub.id,
              description: sub.description,
              price: parseFloat(sub.price) || 0,
              discount: parseFloat(sub.discount) || 0
            })),
            hasDocument: service.templatePath ? true : false,
            note: !service.templatePath ? "Aún no se ha configurado una plantilla de cotización en este servicio. Puede agregar la plantilla en el menú 'Catálogo de servicios' -> 'Editar' -> 'Plantilla Word' -> 'Agregar plantilla'" : null,
            active: service.active,
            riskLevel: service.riskLevel || null,
            packageNumber: index + 1
          };
        })
      );
      
      setServices(servicesWithSubservices);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Error al cargar los servicios');
    } finally {
      setLoading(false);
    }
  };

  // Cargar servicios al montar el componente
  useEffect(() => {
    fetchServices();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + services.length) % services.length);
  };

  const getVisibleServices = () => {
    if (services.length === 0) return [];
    
    // Siempre mostrar exactamente 3 servicios
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % services.length;
      visible.push(services[index]);
    }
    return visible;
  };

  const handleServiceSelect = (service) => {
    setSelectedServiceId(service.id);
    if (onServiceSelect) {
      onServiceSelect(service);
    }
  };


  // Mostrar loading
  if (loading) {
    return (
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando servicios...</Typography>
      </Box>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchServices} variant="outlined">
          Reintentar
        </Button>
      </Box>
    );
  }

  // Mostrar mensaje si no hay servicios
  if (services.length === 0) {
    return (
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No hay servicios disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Agregue servicios en el catálogo para mostrarlos aquí
        </Typography>
      </Box>
    );
  }

  // Verificar si un servicio está seleccionado
  const isServiceSelected = (serviceId) => {
    return selectedServices.some(s => s.id === serviceId);
  };

  return (
    <Box sx={{ mb: compact ? 2 : 4 }}>
      {!compact && (
        <Typography variant="h4" align="center" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50' }}>
          Servicios ({services.length})
        </Typography>
      )}
      
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: compact ? 1 : 2 }}>
        {/* Botón anterior */}
        <IconButton 
          onClick={prevSlide}
          sx={{ 
            bgcolor: '#f5f5f5', 
            '&:hover': { bgcolor: '#e0e0e0' },
            boxShadow: compact ? 1 : 2,
            width: compact ? 32 : 40,
            height: compact ? 32 : 40
          }}
        >
          <ChevronLeft sx={{ fontSize: compact ? 18 : 24 }} />
        </IconButton>

        {/* Contenedor de tarjetas */}
        <Box sx={{ 
          display: 'flex', 
          gap: compact ? 1 : 2, 
          flex: 1,
          overflow: 'hidden',
          width: '100%'
        }}>
          {getVisibleServices().map((service, index) => {
            const isSelected = isServiceSelected(service.id);
            return (
              <Card 
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                sx={{ 
                  flex: 1,
                  minWidth: 0,
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isSelected ? '3px solid #0d47a1' : '1px solid #e0e0e0',
                  boxShadow: isSelected ? '0 6px 18px rgba(13, 71, 161, 0.55)' : '0 2px 6px rgba(0,0,0,0.1)',
                  borderRadius: compact ? 2 : 3,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  cursor: 'pointer',
                  bgcolor: isSelected ? '#bbdefb' : 'white',
                  '&:hover': {
                    transform: compact ? 'translateY(-2px)' : 'translateY(-4px)',
                    boxShadow: isSelected ? '0 8px 22px rgba(13, 71, 161, 0.65)' : '0 6px 15px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, p: compact ? 1.5 : 3, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  <Box sx={{ position: 'absolute', top: compact ? 6 : 10, left: compact ? 6 : 10, backgroundColor: '#e3f2fd', color: '#1565c0', borderRadius: '4px', px: 0.7, py: 0.2, fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: 0.3 }}>P{service.packageNumber}</Box>
                  {/* Título y precio */}
                  <Typography variant={compact ? "subtitle2" : "h6"} sx={{ fontWeight: 'bold', mb: compact ? 1 : 2, textAlign: 'center', fontSize: compact ? '0.9rem' : undefined }}>
                    {service.title}
                  </Typography>
                  
                  <Typography 
                    variant={compact ? "h6" : "h4"}
                    sx={{ 
                      fontWeight: 'bold', 
                      textAlign: 'center', 
                      mb: compact ? 0.5 : 1,
                      color: '#2c3e50',
                      fontSize: compact ? '1.1rem' : undefined
                    }}
                  >
                    ${service.price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </Typography>
                  
                  <Typography 
                    variant="caption"
                    sx={{ textAlign: 'center', mb: compact ? 1 : 3, color: '#666', fontSize: compact ? '0.7rem' : undefined }}
                  >
                    Sin iva.
                  </Typography>

                  {/* Características - solo mostrar en modo no compacto o las primeras 2 */}
                  <Box sx={{ flex: 1, maxHeight: compact ? 160 : 240, overflowY: "auto", pr: 0.5, "&::-webkit-scrollbar": { width: 4 }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: 2 } }}>
                    {service.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: compact ? 0.5 : 1 }}>
                        <Check sx={{ color: '#4caf50', fontSize: compact ? 12 : 16, mr: compact ? 0.5 : 1, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: compact ? '0.7rem' : '0.85rem', lineHeight: 1.4 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}

                    {/* Subservicios - solo en modo no compacto */}
                    {!compact && service.subservices && service.subservices.length > 0 && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 1, color: '#2c3e50' }}>
                          Subservicios incluidos:
                        </Typography>
                        {service.subservices.slice(0, 3).map((sub, idx) => (
                          <Box key={sub.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, ml: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#555', flex: 1 }}>
                              • {sub.description}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#2196f3', fontWeight: 'bold', ml: 1 }}>
                              ${sub.price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        ))}
                        {service.subservices.length > 3 && (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', ml: 1, fontStyle: 'italic' }}>
                            +{service.subservices.length - 3} subservicios más...
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Documento - solo en modo no compacto */}
                  {!compact && service.hasDocument && (
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                      <IconButton sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                        <Download />
                      </IconButton>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Descargar documento
                      </Typography>
                    </Box>
                  )}

                  {/* Nota - solo en modo no compacto */}
                  {!compact && service.note && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#666', 
                        fontStyle: 'italic', 
                        textAlign: 'center',
                        mb: 2,
                        fontSize: '0.75rem'
                      }}
                    >
                      {service.note}
                    </Typography>
                  )}
                </CardContent>

              </Card>
            );
          })}
        </Box>

        {/* Botón siguiente */}
        <IconButton 
          onClick={nextSlide}
          sx={{ 
            bgcolor: '#f5f5f5', 
            '&:hover': { bgcolor: '#e0e0e0' },
            boxShadow: compact ? 1 : 2,
            width: compact ? 32 : 40,
            height: compact ? 32 : 40
          }}
        >
          <ChevronRight sx={{ fontSize: compact ? 18 : 24 }} />
        </IconButton>
      </Box>

      {/* Indicadores - solo en modo no compacto */}
      {!compact && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
          {services.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? '#2196f3' : '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: index === currentIndex ? '#1976d2' : '#bdbdbd'
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ServiceCarousel;
